import { useCallback, useEffect, useState } from "react";

/**
 * useCompareShortlist — a tiny LocalStorage-backed shortlist for the
 * "Pin to Compare" flow on UniversityDetail. Caps at 4 to line up
 * with the existing /compare?u1..u4 URL scheme.
 *
 * Usage:
 *   const { ids, toggle, has, remove, clear, compareHref } = useCompareShortlist();
 */
const KEY = "wua:compareShortlist";
const CAP = 4;

// Cross-tab and cross-component sync: components subscribe to the
// same custom event so the floating bar updates when the detail
// page toggles the pin.
const EVT = "wua:compareShortlist:change";

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => typeof x === "string").slice(0, CAP);
  } catch { return []; }
}

function write(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next.slice(0, CAP)));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {}
}

export function useCompareShortlist() {
  const [ids, setIds] = useState(read);

  useEffect(() => {
    const sync = () => setIds(read());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    const cur = read();
    let next;
    if (cur.includes(id)) next = cur.filter((x) => x !== id);
    else if (cur.length >= CAP) next = cur; // full — no-op
    else next = [...cur, id];
    write(next);
    setIds(next);
    return next;
  }, []);

  const remove = useCallback((id) => {
    const next = read().filter((x) => x !== id);
    write(next);
    setIds(next);
  }, []);

  const clear = useCallback(() => {
    write([]);
    setIds([]);
  }, []);

  const compareHref = ids.length >= 2
    ? "/compare?" + ids.map((id, i) => `u${i + 1}=${encodeURIComponent(id)}`).join("&")
    : null;

  return { ids, has, toggle, remove, clear, compareHref, cap: CAP, full: ids.length >= CAP };
}
